import {
  ArrowRightCircleIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { vehicleDisplayIdentifier } from "../../utils/vehicleIdentifier";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { IPlannerSearchResult, IPlannerVehicleRow } from "../../models/ProductionPlanner";
import { VehicleStatus } from "../../models/Vehicle";
import classNames from "../../utils/classNames";

interface PlannerSearchSummaryProps {
  searchTerm: string;
  // Matches that are on the planner within the days and bays currently on screen.
  plannerMatches: IPlannerVehicleRow[];
  bayNameById: Map<string, string>;
  awaitingMatchCount: number;
  // False while the term is too short for the server lookup, so the bar doesn't
  // claim there's no match when it hasn't actually looked.
  canLookup: boolean;
  isLookingUp: boolean;
  lookupResults: IPlannerSearchResult[];
  onFocusVehicle: (vehicleId: string) => void;
  onGoToResult: (result: IPlannerSearchResult) => void;
  onClear: () => void;
}

const dayLabel = (iso: string): string =>
  DateTime.fromISO(iso, { zone: "utc" }).toFormat("ccc d LLL");

const vehicleLabel = (
  vrm: string | null,
  make: string | null,
  model: string | null
): string => {
  const description = [make, model].filter(Boolean).join(" ");
  if (!vrm) return description;
  return description.length > 0 ? `${vrm} · ${description}` : vrm;
};

export default function PlannerSearchSummary({
  searchTerm,
  plannerMatches,
  bayNameById,
  awaitingMatchCount,
  canLookup,
  isLookingUp,
  lookupResults,
  onFocusVehicle,
  onGoToResult,
  onClear,
}: PlannerSearchSummaryProps) {
  const { t } = useTranslation("production");

  if (searchTerm.trim().length === 0) return null;

  const onScreenCount = plannerMatches.length + awaitingMatchCount;
  const booked = lookupResults.filter((r) => r.plannedDismantlingOn !== null);
  const elsewhere = lookupResults.filter((r) => r.plannedDismantlingOn === null);

  const tone =
    onScreenCount > 0
      ? "bg-primary-50 border-primary-200 text-primary-900"
      : booked.length > 0
        ? "bg-amber-50 border-amber-200 text-amber-900"
        : "bg-gray-50 border-gray-200 text-gray-700";

  const chipClasses =
    "inline-flex items-center gap-x-1 rounded border border-primary-300 bg-white px-1.5 py-0.5 text-xs font-medium text-primary-800 hover:bg-primary-100";

  return (
    <div
      className={classNames(
        "rounded-md border px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm",
        tone
      )}
    >
      <MagnifyingGlassIcon className="h-4 w-4 shrink-0" />

      {onScreenCount > 0 ? (
        <>
          {plannerMatches.length > 0 && (
            <span className="font-medium">
              {plannerMatches.length === 1
                ? t("1 match on the planner")
                : t("{{count}} matches on the planner", { count: plannerMatches.length })}
            </span>
          )}
          {plannerMatches.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => onFocusVehicle(v.id)}
              className={chipClasses}
              title={t("Show on the planner")}
            >
              <ArrowRightCircleIcon className="h-3.5 w-3.5" />
              {vehicleDisplayIdentifier(v) || t("No VRM")}
              {v.dismantlingBayId && bayNameById.has(v.dismantlingBayId) && (
                <span className="font-normal text-primary-600">
                  {bayNameById.get(v.dismantlingBayId)}
                </span>
              )}
              {v.plannedDismantlingOn && (
                <span className="font-normal text-primary-600">
                  {dayLabel(v.plannedDismantlingOn)}
                </span>
              )}
            </button>
          ))}
          {awaitingMatchCount > 0 && (
            <span className="text-xs">
              {awaitingMatchCount === 1
                ? t("1 awaiting dismantling")
                : t("{{count}} awaiting dismantling", { count: awaitingMatchCount })}
            </span>
          )}
        </>
      ) : isLookingUp ? (
        <span>{t("Checking the rest of the planner...")}</span>
      ) : !canLookup ? (
        <span>{t("Keep typing to search the rest of the planner.")}</span>
      ) : booked.length > 0 ? (
        <>
          <span className="font-medium">{t("Not on this screen — already booked in:")}</span>
          {booked.slice(0, 4).map((r) => (
            <span key={r.id} className="inline-flex items-center gap-x-1.5">
              <span className="text-xs">
                {t("{{vehicle}} — {{bay}} on {{date}}", {
                  vehicle: vehicleLabel(r.vrm, r.combinedMake, r.combinedModel),
                  bay: r.dismantlingBayName ?? t("no bay"),
                  date: dayLabel(r.plannedDismantlingOn as string),
                })}
              </span>
              <button
                type="button"
                onClick={() => onGoToResult(r)}
                className="inline-flex items-center gap-x-1 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                <CalendarDaysIcon className="h-3.5 w-3.5" />
                {t("Go to day")}
              </button>
            </span>
          ))}
        </>
      ) : elsewhere.length > 0 ? (
        <>
          <span className="font-medium">{t("Not booked in:")}</span>
          {elsewhere.slice(0, 4).map((r) => (
            <span key={r.id} className="text-xs">
              {t("{{vehicle}} — {{status}}", {
                vehicle: vehicleLabel(r.vrm, r.combinedMake, r.combinedModel),
                status: r.isAwaitingDismantling
                  ? t("awaiting dismantling")
                  : (VehicleStatus[r.status] ?? t("unknown")),
              })}
              {r.locationName && (
                <span className="text-gray-500"> · {r.locationName}</span>
              )}
            </span>
          ))}
        </>
      ) : (
        <span>{t('No vehicles match "{{term}}"', { term: searchTerm.trim() })}</span>
      )}

      <button
        type="button"
        onClick={onClear}
        className="ml-auto inline-flex items-center gap-x-1 text-xs text-gray-600 hover:text-gray-900"
      >
        <XMarkIcon className="h-3.5 w-3.5" />
        {t("Clear search")}
      </button>
    </div>
  );
}
